import type { ReactNode } from "react";
import { Link } from "wouter";
import { AlertTriangle, Lock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { ErroApi } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";

/**
 * Carregando, erro, ou o conteúdo — a decisão em UM lugar.
 *
 * ⚠️ O PADRÃO ANTIGO ERA UM BUG: `isLoading || !data ? <PageSkeleton/> : …`
 * mantém o esqueleto PARA SEMPRE quando a consulta falha, porque aí `isLoading`
 * é false e `data` é `undefined`. Verificado em produção com a org PRODUTOR:
 * `/mercado/cargas` respondia 403 e a tela ficava nos blocos cinza, com a pessoa
 * esperando algo que nunca vem. Esqueleto eterno é pior que mensagem de erro —
 * a mensagem pelo menos encerra a espera.
 *
 * Por isso a decisão não fica em cada tela: `estadoDaConsulta` recebe o retorno
 * do `useQuery` e devolve o que renderizar no lugar do conteúdo, ou `null` quando
 * o conteúdo pode aparecer. A tela faz uma coisa só:
 *
 *     const aguardando = estadoDaConsulta(q, { variante: "painel" });
 *     if (aguardando) return <CompradorShell>{aguardando}</CompradorShell>;
 *
 * ou, em tela que já monta cabeçalho antes dos dados:
 *
 *     {aguardando ?? <>…conteúdo…</>}
 */

/** O mínimo do `useQuery` que a decisão precisa — não o objeto inteiro. */
export interface ConsultaParcial {
  isLoading: boolean;
  isError?: boolean;
  error?: unknown;
  data?: unknown;
}

/**
 * Qual dos três estados a consulta está — a decisão, sem JSX.
 *
 * ⚠️ A ORDEM DOS TESTES É O BUG. Consulta em erro tem `isLoading` false E `data`
 * undefined; quem checasse `!data` primeiro cairia no esqueleto e ficaria nele
 * para sempre. Erro vem antes, e é isto que o teste fixa.
 */
export type EstadoConsulta = "erro" | "carregando" | "pronto";

export function classificarConsulta(consulta: ConsultaParcial): EstadoConsulta {
  if (consulta.isError || consulta.error) return "erro";
  // `enabled: false` deixa a consulta parada: nem carregando, nem com dado. Aí o
  // esqueleto é o certo — é o que a tela mostra enquanto a condição não vale.
  if (consulta.isLoading || consulta.data === undefined) return "carregando";
  return "pronto";
}

export function estadoDaConsulta(
  consulta: ConsultaParcial,
  opcoes?: { variante?: "tabela" | "cards" | "painel"; linhas?: number },
): ReactNode | null {
  const estado = classificarConsulta(consulta);
  if (estado === "erro") return <ErroDaConsulta erro={consulta.error} />;
  if (estado === "carregando") {
    return <PageSkeleton variante={opcoes?.variante ?? "painel"} linhas={opcoes?.linhas} />;
  }
  return null;
}

/**
 * O que dizer sobre o erro — PURO, para poder ser testado sem DOM.
 *
 * ⚠️ 403 DE ÁREA ERRADA TEM TEXTO PRÓPRIO. "Erro inesperado" para o produtor que
 * abriu uma tela de comprador é falso: não houve erro nenhum, ele está no lugar
 * errado e o app sabe qual é o lugar certo. Esconder o item no menu resolve para
 * quem navega; isto é para quem chega pela URL (link velho, WhatsApp, histórico).
 */
export interface ExplicacaoErro {
  titulo: string;
  descricao?: string;
  /** Para onde ir. `null` = não há saída melhor que tentar de novo. */
  href: string | null;
  rotuloAcao?: string;
  /** `true` quando a saída é recarregar (falha transitória). */
  recarregar?: boolean;
  bloqueio: boolean;
  testId: string;
}

export function explicarErro(erro: unknown, orgTipo?: string | null): ExplicacaoErro {
  const api = erro instanceof ErroApi ? erro : null;
  const code = api?.code ?? null;

  if (code === "ORG_TIPO_INVALIDO") {
    // Qual é a área certa depende de que lado do balcão a org está.
    const ehComprador = orgTipo === "COMPRADOR";
    return ehComprador
      ? {
        titulo: "Esta tela é do produtor.",
        descricao: "Suas cargas e romaneios recebidos estão no Início do Mercado.",
        href: "/mercado", rotuloAcao: "Ir para o Início", bloqueio: true, testId: "erro-org-tipo",
      }
      : {
        titulo: "Esta tela é do comprador.",
        descricao: "Seus romaneios estão em A receber.",
        href: "/mercado/receber", rotuloAcao: "Ir para A receber", bloqueio: true, testId: "erro-org-tipo",
      };
  }

  if (code === "PERFIL_INVALIDO") {
    return {
      titulo: "Esta tela não é do seu perfil.",
      // A frase é a do SERVIDOR: ele sabe qual perfil recusou e por quê, e
      // duplicá-la aqui criaria duas versões do mesmo texto para manter.
      descricao: api?.mensagem,
      href: "/mercado/pagar", rotuloAcao: "Ir para A pagar", bloqueio: true, testId: "erro-perfil",
    };
  }

  if (code === "MODULE_NOT_ENABLED") {
    return {
      titulo: "O Mercado não está ativo para esta organização.",
      descricao: "Fale com o administrador da conta para liberar o módulo.",
      href: null, bloqueio: true, testId: "erro-modulo",
    };
  }

  if (api?.status === 401) {
    return {
      titulo: "Sua sessão expirou.",
      descricao: "Entre de novo para continuar.",
      href: "/login", rotuloAcao: "Entrar", bloqueio: true, testId: "erro-sessao",
    };
  }

  if (api?.status === 404) {
    return {
      titulo: "Não encontrei isso.",
      descricao: api.mensagem || "O registro pode ter sido removido.",
      href: null, bloqueio: true, testId: "erro-nao-encontrado",
    };
  }

  // Falha transitória, queda de rede, 500. Recarregar é a saída honesta: o app
  // não sabe o que deu errado, e insistir é o que costuma resolver.
  return {
    titulo: "Não consegui carregar.",
    descricao: api?.mensagem || "Verifique a conexão e tente de novo.",
    href: null, recarregar: true, bloqueio: false, testId: "erro-consulta",
  };
}

export function ErroDaConsulta({ erro }: { erro: unknown }) {
  const { user } = useAuth();
  const e = explicarErro(erro, user?.orgTipo ?? null);

  return (
    <Painel
      icone={e.bloqueio ? Lock : AlertTriangle}
      titulo={e.titulo}
      descricao={e.descricao}
      acao={
        e.href
          ? <Button asChild size="sm"><Link href={e.href}>{e.rotuloAcao}</Link></Button>
          : e.recarregar
            ? (
              <Button
                size="sm" variant="outline"
                onClick={() => window.location.reload()}
                data-testid="button-recarregar"
              >
                <RefreshCw className="h-4 w-4 mr-1" />Tentar de novo
              </Button>
            )
            : undefined
      }
      testId={e.testId}
    />
  );
}

function Painel({
  icone: Icone, titulo, descricao, acao, testId,
}: {
  icone: typeof AlertTriangle;
  titulo: string;
  descricao?: string;
  acao?: ReactNode;
  testId: string;
}) {
  return (
    <div
      className="rounded-lg border bg-card py-10 px-4 flex flex-col items-center justify-center text-center gap-2"
      data-testid={testId}
    >
      <Icone className="h-7 w-7 text-muted-foreground" />
      <p className="font-medium text-sm">{titulo}</p>
      {descricao && <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">{descricao}</p>}
      {acao && <div className="mt-2">{acao}</div>}
    </div>
  );
}
